import _ from "utilities/vendor/lodash/4.13.1/js/lodash.custom.min";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.0.4/js/sparta-store-utility";
import "utilities/vendor/fetch/2.0.3/js/fetch";
/**
 * @name        JCR Utility
 * @exports     jcrUtility
 * @description Utility to fetch and manage JSON Content Records.
 * @author      Sunny Singh (ZKTUUYV)
 * @copyright   2018-9-14
 * @file
 */

/* eslint-disable import/prefer-default-export, max-len */
// NOTE: A lot of this code has been abstracted from the `sales-demo/module-demo` module.
// Match unauth http or https bankofamerica.com domain for dev, devflex, qa, cert, preview, or review environments.
const DOMAIN_PATTERN = /^https?:\/\/www(?:-(?:(?:dev(?:flex)?)|(?:qa)|(?:cert)|(?:p?review))\d?\.ecnp)?\.bankofamerica.com/gi;
const WEB_JCR_TOKEN_PATTERN = /\\?\{(JCR|GLOSSARY|FOOTNOTE)\|\|((?:(?:\{(?:(?:\{(?:[^{}])*\})|(?:[^{}]))*\})|(?:[^{}]))*)(?:\|\| *(freeform) *)*\}/g;
const DEFAULT_GATEWAY_URL = 'ogateway/public/v1/content';

/**
 * To get Jcr manifest.
 * @param {string} siteName Site name.
 * @param {Object} spaParameters Spa parameters.
 * @returns {Promise<Object>} Jcr manifest object.
 */
async function getJCRManifest(siteName, spaParameters = {}) {
  const { buildTimestamp, siteParams } = spaParameters;
  const isJcrManifestTimestamped = buildTimestamp;
  let isCacheRequired = false;
  let jcrManifestTimestamp = '';

  if (isJcrManifestTimestamped) {
    isCacheRequired = siteParams.env !== 'local';
    jcrManifestTimestamp = `-${buildTimestamp}`;
  }

  const jcrManifestEndpoint = `/${siteName}/spa-assets/jcr-manifest${jcrManifestTimestamp}.json`;
  const manifest = await fetch(jcrManifestEndpoint, {
    // by default cache is on , to disable cache we need to add header cache: 'no-cache'
    ...isCacheRequired ? undefined : { cache: 'no-cache' },
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    mode: 'same-origin',
  });

  return manifest.json();
}

/**
 * To get resolved environment variables.
 * It will resolve [] syntax in string using environment variables.
 * @param {string} url Url string
 * @param {Object} environmentVariables environment variables.
 * @returns {string} resolved url string.
 */
function resolveEnvironmentVariableInUrl(url, environmentVariables) {
  const compiled = _.template(url, { interpolate: /\{([\s\S]+?)\}/g });
  return compiled(environmentVariables);
}

/**
 * To get resolved path for given JCR tokens.
 * @param {Array<string>} token Parsed Jcr token.
 * @param {Object} spaParameters Spa parameters.
 * @param {Object} manifest Jcr manifest object.
 * @returns {Object} Path data object.
 */
function getWebJcrPath(token, spaParameters, manifest) {
  const jcrToken = _.first(token);
  const [jcrTokenKey, jcrTokenHelperFlag = 'shield'] = _.map(_.split(token[2], '||'), _.trim);
  const environmentVariables = { ...spaParameters.siteParams, language: window.sparta.language };
  let url;

  if (jcrTokenHelperFlag === 'shield') {
    const { env: environment, gatewayUrl = DEFAULT_GATEWAY_URL, language, shieldHost } = environmentVariables;
    if (!shieldHost) {
      throw new Error(`'shieldHost' is not present in '${environment}' environment variables`);
    }
    const resolvedJcrTokenKey = resolveEnvironmentVariableInUrl(
      jcrTokenKey, environmentVariables,
    );
    const lang = /^(es|en)\.(.)*/.test(jcrTokenKey) ? '' : `${language}.`;
    url = new URL(`${gatewayUrl}?key=${lang}${resolvedJcrTokenKey}`, shieldHost);
  } else if (jcrTokenHelperFlag === 'freeform') {
    url = new URL(resolveEnvironmentVariableInUrl(jcrTokenKey, environmentVariables));
  } else {
    throw new Error(`Helper flag '${jcrTokenHelperFlag}' is not supported in JCR token: ${jcrToken}`);
  }

  url = escape(url.href).replace(/\//g, '-');
  let pathData = manifest.jcrs.find(obj => obj.path.includes(url));
  if (pathData) {
    pathData = _.mapValues(pathData, value => escape(value));
  }

  return pathData;
}

/**
 * Resolves data sources to compiled paths.
 * @example
 *  const dataSources = await jcrUtility.resolveDataSources({
 *    data1: 'site/credit-cards/shared/{language}/product-language-map.json'
 *  });
 *
 *  console.log(dataSources);
 *  // Output: { data1: '/credit-cards/spa-assets/data/site-credit-cards-module-specific-compare-cc-module-en-compare-cc-module-en-T1536958978313.json' }
 * @param {String} siteName Site name (e.g. credit-cards).
 * @param {Object} dataSources JCRs to fetch.
 * @returns {Promise} Returns a Promise that resolves the `dataSources` object with compiled paths.
 */
export async function resolveDataSources(siteName, dataSources) {
  // Added Store depdencies to fetch spaParams. spaParams can come from Widget or Page. It been computed and added by BootStrap utility.
  // We need clean up of this JCR Utility to make it consistent with other global utilities.
  const spaParameters = spartaStoreUtility.getData('spaParams') || spaParams;
  const manifest = await getJCRManifest(siteName, spaParameters);
  const jcrs = {};

  Object.keys(dataSources).filter(key => dataSources[key]).forEach((key) => {
    const jcrTokens = new RegExp(WEB_JCR_TOKEN_PATTERN).exec(dataSources[key]);
    let pathData;
    if (jcrTokens) {
      pathData = getWebJcrPath(jcrTokens, spaParameters, manifest);
    } else {
      const formattedPath = dataSources[key]
        .replace('{language}', window.sparta.language)
        .replace(/\//g, '-')
        .replace(/.json$/, '');
      pathData = manifest.jcrs.find(obj => obj.path.includes(formattedPath));
    }

    if (pathData) {
      jcrs[key] = pathData.path.replace(DOMAIN_PATTERN, '').replace('http://localhost/', '/').replace(`/${spaParameters.siteName}/`, `/${siteName}/`);
    }
  });

  return Object.assign({}, dataSources, jcrs);
}